import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, Renderer2 } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { TimepickerComponent } from '../../../../shared/components/timepicker/timepicker.component';
import { TimesheetUnitsHandler } from '../timesheets-add-entries/timesheet-units.handler';

@Component({
  selector: 'ppl-timesheet-adjust-void-entry',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FormsModule,
    TableModule,
    NzToolTipModule,
    CalendarModule,
    DropdownSelectComponent,
    TimepickerComponent
  ],
  templateUrl: './timesheet-adjust-void-entry.component.html',
  styleUrl: './timesheet-adjust-void-entry.component.css',
  providers: [TimesheetUnitsHandler]
})
export class TimesheetAdjustVoidEntryComponent {
  @Input() timesheetData?: any;
  @Input() searchParams: any;
  @Input() actionData: any;
  @Input() source?: string;
  @Output() timesheetAdjustedOrVoided = new EventEmitter();
  adjustVoidTimesheetForm!: FormGroup;
  reasonList: any;
  buttonLabel = '';
  actionType = '';
  serviceCodes: LookupValueItem[] = [];
  programName!: string;
  message: string = '';
  mcoList: any;
  reasonText = '';
  timeInTimeOutEqualMessage = 'Service Time In cannot be equal to Service Time Out.';
  timeOutLessThanTimeInMessage = 'Service Time In cannot be greater than Service Time Out.';
  timeOutOverNightShiftMessage = 'Overnight shifts must be entered separately after midnight (12:00 AM).';
  private billedUnitsCache = new Map<string, number | null>();

  constructor(
    private fb: FormBuilder,
    private dateTimeFormatterService: DateTimeFormatterService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService,
    private timesheetUnitsHandler: TimesheetUnitsHandler,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private doc: Document
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';

    this.actionType = this.actionData.buttonAction;
    this.buttonLabel = this.actionData.button_label;
    this.reasonList = this.source === 'payroll' || this.source === 'billing' ? this.lookupService.getLookupValue('adjustment_reason')
      : this.lookupService.getLookupValue('resurrect_reason');
    this.reasonText = this.actionType.indexOf('adjust') > -1 ? 'Adjust reason' : this.actionType.indexOf('void') > -1 ? 'Void reason' : 'Resurrect reason';

    this.adjustVoidTimesheetForm = this.fb.group({
      serviceDate: [null],
      serviceCode: [null],
      timeIn: [null],
      timeOut: [null],
      billedUnits: [null],
      reason: [null, [Validators.required]],
      mcoName: [null],
    });

    if (this.source == 'payroll' || this.source == 'resurrect') {
      this.setPayrollResurrectConfiguration();
    } else {
      this.setBillingConfiguration();
    }

    if (this.actionType === 'adjustAllTimesheet' || this.actionType === 'voidAllTimesheets' || this.actionType === 'adjustAllBilling' ||
      this.actionType === 'voidAllBillings' || this.actionType == 'adjustAllBillings' || this.actionType === 'resurrectAllTimesheet') {
      if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
        this.message = `Note: The ${this.actionData.button_label} action will be performed on all selected timesheets.`;
      } else {
        this.message = `Note: The ${this.actionData.button_label} action will be performed on all timesheets matching the selected search criteria.`;
      }
    }
  }

  setBillingConfiguration() {
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.mcoList = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
      }
    });
  }

  setPayrollResurrectConfiguration() {
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');

    this.adjustVoidTimesheetForm.get('billedUnits')?.disable();

    if (this.actionType === 'adjustTimesheet' || this.actionType === 'resurrectTimesheet') {
      this.adjustVoidTimesheetForm?.patchValue({
        serviceDate: this.timesheetData?.serviceDate,
        serviceCode: this.timesheetData?.serviceCode,
        timeIn: this.timesheetData?.timeIn ? this.convertTimeStringToDate(this.timesheetData.timeIn) : null,
        timeOut: this.timesheetData?.timeOut ? this.convertTimeStringToDate(this.timesheetData.timeOut) : null,
        billedUnits: this.timesheetData?.billedUnits
      });

      this.adjustVoidTimesheetForm.get('serviceDate')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceCode')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('timeIn')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('timeOut')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('billedUnits')?.setValidators([Validators.required]);

      Object.keys(this.adjustVoidTimesheetForm.controls).forEach(controlName => {
        if (controlName !== 'billedUnits' && controlName !== 'reason') {
          this.adjustVoidTimesheetForm.get(controlName)?.valueChanges.subscribe(() => {
            this.updateTimesheetUnits(this.adjustVoidTimesheetForm);
          });
        }
      });

      this.timesheetData.timeIn = this.dateTimeFormatterService.convertTo12HourFormat(this.timesheetData.timeIn);
      this.timesheetData.timeOut = this.dateTimeFormatterService.convertTo12HourFormat(this.timesheetData.timeOut);
    }
  }

  isTimeInAndTimeOutInvalid(timeIn: Date, timeOut: Date) {
    this.adjustVoidTimesheetForm.get('timeIn')?.setErrors(null);
    this.adjustVoidTimesheetForm.get('timeOut')?.setErrors(null);

    const inHours = timeIn.getHours();
    const inMinutes = timeIn.getMinutes();
    const outHours = timeOut.getHours();
    const outMinutes = timeOut.getMinutes();

    const totalMinutesIn = inHours * 60 + inMinutes;
    const totalMinutesOut = outHours * 60 + outMinutes;

    if (totalMinutesIn === totalMinutesOut) {
      this.adjustVoidTimesheetForm.get('timeIn')?.setErrors({ customError: this.timeInTimeOutEqualMessage });

      return true;
    }

    if (totalMinutesOut < totalMinutesIn) {
      if (outHours < 8 || (outHours === 0 && outMinutes === 0)) {
        this.adjustVoidTimesheetForm.get('timeOut')?.setErrors({ customError: this.timeOutOverNightShiftMessage });
      } else {
        this.adjustVoidTimesheetForm.get('timeOut')?.setErrors({ customError: this.timeOutLessThanTimeInMessage });
      }

      return true;
    }

    return false;
  }

  getServiceCodeDetails(item: any) {
    const regex = /^([^:]+)(?::(\S+))?/;
    const match = item.lkpValueName.match(regex);
    if (match) {
      return {
        serviceCode: match[1].trim(),
        modifierCode: match[2] ? match[2].trim() : ""
      };
    }
    return {
      serviceCode: item.lkpValueName.trim(),
      modifierCode: ""
    };
  }

  updateTimesheetUnits(entry: FormGroup): void {
    const serviceTimeIn = entry.get('timeIn')?.value;
    const serviceTimeOut = entry.get('timeOut')?.value;
    const serviceCode = entry.get('serviceCode')?.value;
    const serviceDate = entry.get('serviceDate')?.value;
    const updatedServiceCode = this.getServiceCodeDetails(serviceCode);

    if (!serviceDate || !serviceCode || !serviceTimeIn || !serviceTimeOut) {
      entry.get('billedUnits')?.setValue(null);
      return;
    }

    const correctedTimeIn = this.overrideDateWithServiceDate(serviceTimeIn, serviceDate);
    const correctedTimeOut = this.overrideDateWithServiceDate(serviceTimeOut, serviceDate);
    if (correctedTimeOut < correctedTimeIn) {
      entry.get('billedUnits')?.setValue(null);
      return;
    }

    if (updatedServiceCode.serviceCode === 'SSL') {
      const billedUnits = this.timesheetUnitsHandler.calculateBilledUnits(
        correctedTimeIn,
        correctedTimeOut,
        updatedServiceCode.serviceCode,
        serviceCode?.lkpValueCode
      );
      entry.get('billedUnits')?.setValue(billedUnits);
      return;
    }

    const payload = {
      ServiceCode: updatedServiceCode.serviceCode,
      Modifier: updatedServiceCode.modifierCode,
      ShiftStart: this.dateTimeFormatterService.formatDate(correctedTimeIn, 'yyyy-MM-ddTHH:mm:ss'),
      ShiftEnd: this.dateTimeFormatterService.formatDate(correctedTimeOut, 'yyyy-MM-ddTHH:mm:ss'),
    };

    const cacheKey = `${payload.ServiceCode}|${payload.Modifier}|${payload.ShiftStart}|${payload.ShiftEnd}`;
    if (this.billedUnitsCache.has(cacheKey)) {
      if (this.billedUnitsCache.get(cacheKey) !== -1) {
        entry.get('billedUnits')?.setValue(this.billedUnitsCache.get(cacheKey));
      }
      return;
    }

    this.billedUnitsCache.set(cacheKey, -1); // Set to -1 to indicate that the value is being fetched
    this.timesheetService.getBilledUnits(this.programName, payload).subscribe((data: any) => {
      const billedUnits = data?.responsedata?.units ?? null;
      this.billedUnitsCache.set(cacheKey, billedUnits);
      entry.get('billedUnits')?.setValue(billedUnits);
    });
  }

  private overrideDateWithServiceDate(time: Date, serviceDate: string): Date {
    if (!serviceDate) return time;
    const date = new Date(serviceDate);
    const corrected = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), 0, 0);
    return corrected;
  }

  convertTimeStringToDate(timeString: string): Date {
    const currentDate = new Date();
    const [hours, minutes] = timeString.split(':');
    currentDate.setHours(Number(hours), Number(minutes), 0, 0); // Set the hours and minutes
    return currentDate;
  }

  onSave(): void {
    if (this.adjustVoidTimesheetForm?.valid) {
      let placeholder = '';
      let urlEndpoint = '';
      const payload: any = {
        reason: this.adjustVoidTimesheetForm?.get('reason')?.value?.lkpValueCode
      };

      if (this.source == 'payroll' || this.source == 'resurrect') {
        const serviceTimeIn = this.adjustVoidTimesheetForm?.get('timeIn')?.value;
        const serviceTimeOut = this.adjustVoidTimesheetForm?.get('timeOut')?.value;

        if ((this.actionData.buttonAction === 'adjustTimesheet' || this.actionData.buttonAction === 'resurrectTimesheet') &&
          this.isTimeInAndTimeOutInvalid(new Date(serviceTimeIn), new Date(serviceTimeOut))) {
          return;
        }

        switch (this.actionData.buttonAction) {
          case 'adjustTimesheet': {
            placeholder = 'Timesheet adjusted';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_ADJUST;
            payload.timesheetId = this.timesheetData?.tas_timesheetline_detail_id;
            payload.dos = this.dateTimeFormatterService.formatToString(new Date(this.adjustVoidTimesheetForm?.get('serviceDate')?.value));
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            payload.clockIn = this.dateTimeFormatterService.formatTime(serviceTimeIn);
            payload.clockOut = this.dateTimeFormatterService.formatTime(serviceTimeOut);
            break;
          }
          case 'adjustAllTimesheet': {
            placeholder = 'Timesheet adjusted';
            if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_ADJUST_TIMESHEET;
              payload.timesheetIDs = this.timesheetData?.tas_timesheetline_detail_ids;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_ADJUST_ALL_TIMESHEET;
              payload.searchParam = this.searchParams;
            }
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            break;
          }
          case 'voidTimesheet': {
            placeholder = 'Timesheet voided';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_TIMESHEET;
            payload.timesheetIDs = [this.timesheetData?.tas_timesheetline_detail_id];
            break;
          }
          case 'voidAllTimesheets': {
            placeholder = 'Timesheet voided';
            if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
              payload.timesheetIDs = this.timesheetData?.tas_timesheetline_detail_ids;
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_TIMESHEET;
            } else {
              payload.searchParam = this.searchParams;
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_ALL_TIMESHEET;
            }
            break;
          }
          case 'resurrectTimesheet': {
            placeholder = 'Timesheet resurrected';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT;
            payload.timesheetId = this.timesheetData?.tas_timesheetline_detail_id;
            payload.dos = this.dateTimeFormatterService.formatToString(new Date(this.adjustVoidTimesheetForm?.get('serviceDate')?.value));
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            payload.clockIn = this.dateTimeFormatterService.formatTime(serviceTimeIn);
            payload.clockOut = this.dateTimeFormatterService.formatTime(serviceTimeOut);
            break;
          }
          case 'resurrectAllTimesheet': {
            placeholder = 'Timesheet resurrected';
            if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT_SELECTED;
              payload.timesheetIDs = this.timesheetData?.tas_timesheetline_detail_ids;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT_SEARCH_CRITERIA;
              payload.searchParam = this.searchParams;
            }
            break;
          }
        }
      } else {
        switch (this.actionData.buttonAction) {
          case 'voidBilling': {
            placeholder = 'Billing voided';
            urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_TIMESHEET;
            payload.timesheetIDs = [this.timesheetData?.tas_timesheetline_detail_id];
            break;
          }
          case 'voidAllBillings': {
            placeholder = 'Billings voided';
            if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
              payload.timesheetIDs = this.timesheetData?.tas_timesheetline_detail_ids;
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_TIMESHEET;
            } else {
              payload.searchParam = this.searchParams;
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_ALL_TIMESHEET;
            }
            break;
          }
          case 'adjustAllBillings': {
            placeholder = 'Billings adjusted';
            if (this.timesheetData?.tas_timesheetline_detail_ids?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_ADJUST_TIMESHEET;
              payload.timesheetIDs = this.timesheetData?.tas_timesheetline_detail_ids;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_ADJUST_ALL_TIMESHEET;
              payload.searchParam = this.searchParams;
            }
            payload.mcoName = this.adjustVoidTimesheetForm?.get('mcoName')?.value;
            break;
          }
        }
      }

      if (urlEndpoint) {
        this.timesheetService.adjustVoidTimesheet(urlEndpoint, this.programName, payload, placeholder)
          .subscribe((data: any) => {
            this.timesheetAdjustedOrVoided.emit({});
          });
      }
    } else {
      this.adjustVoidTimesheetForm.markAllAsTouched();
    }
  }

}
